// SubidaArchivo.jsx
//
// Un papel para subir (`up` del mockup A del alta): un <label> que envuelve un <input type=file>. Tocarlo abre
// el selector, en el celular y en escritorio, y también acepta soltar un archivo encima. Salió de
// VerificationWizard.js en #1809 porque ahora lo usan dos pantallas: el paso de Documentos del wizard y el paso
// «Tus documentos» antes de pagar (components/checkout/PasoDocumentos.jsx), que no deben depender del wizard.
import { useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { Ic } from "../views/profile/PasosBienvenida";

const formatBytes = (bytes) => {
  if (!bytes) return "";
  const units = ["B", "KB", "MB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

// Un papel para subir (`up` del mockup A). Es un <label> que envuelve un
// <input type=file>: tocarlo abre el selector, en el celular y en escritorio, y
// también acepta soltar un archivo encima. Reemplaza al DocumentUploader de
// antes (que tenía colores literales y el look de la tarjeta vieja).
const SubidaArchivo = ({ titulo, detalle, value, onChange, testId, maxSizeMB = 5 }) => {
  const inputRef = useRef(null);
  const [error, setError] = useState("");
  const hasFile = Boolean(value);

  const aceptar = (file) => {
    if (!file) return;
    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`El archivo supera los ${maxSizeMB} MB`);
      return;
    }
    setError("");
    onChange(file);
  };

  return (
    <Box>
      <Box
        component="label"
        data-testid={testId}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          aceptar(e.dataTransfer?.files?.[0]);
        }}
        sx={{
          display: "flex",
          gap: "12px",
          alignItems: "center",
          cursor: "pointer",
          border: "1.5px",
          borderStyle: hasFile ? "solid" : "dashed",
          borderColor: hasFile ? "rgba(var(--color-success-rgb),0.5)" : "rgba(var(--fg-rgb),0.2)",
          borderRadius: "14px",
          p: "13px 14px",
          fontSize: 14.5,
          minHeight: 64,
          "&:focus-within": { outline: "2px solid var(--accent-onboarding)", outlineOffset: 2 },
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*,application/pdf"
          style={{ display: "none" }}
          onChange={(e) => aceptar(e.target.files?.[0])}
        />
        <Box
          component="span"
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            flex: "none",
            display: "grid",
            placeItems: "center",
            backgroundColor: hasFile ? "rgba(var(--color-success-rgb),0.16)" : "rgba(var(--color-primary-rgb),0.16)",
            color: hasFile ? "var(--color-success)" : "var(--accent-onboarding)",
          }}
        >
          {hasFile ? <Ic.Ok /> : <Ic.Up />}
        </Box>
        <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
          {titulo}
          <Box component="small" sx={{ display: "block", color: "var(--text-faint)", fontSize: 12.5, overflowWrap: "anywhere" }}>
            {hasFile ? `${value.name || "Archivo cargado"} · ${formatBytes(value.size)}` : detalle}
          </Box>
        </Box>
        {hasFile && (
          <Box
            component="button"
            type="button"
            aria-label={`Quitar ${titulo}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onChange(null);
              if (inputRef.current) inputRef.current.value = "";
            }}
            sx={{
              flex: "none",
              width: 44,
              height: 44,
              border: 0,
              background: "none",
              color: "var(--text-muted)",
              cursor: "pointer",
              fontSize: 20,
              borderRadius: "50%",
            }}
          >
            ×
          </Box>
        )}
      </Box>
      {error && (
        <Typography variant="caption" sx={{ color: "var(--color-danger)", mt: 0.5, display: "block" }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default SubidaArchivo;
